import { useHoneyGridApiFetch } from '@/queries/useHoneyGridApiFetch'
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b'
import type { B2BOrganizationsUpdateOptions, Organization } from '@stytch/vanilla-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { CreateStytchWorkspace } from 'honeygrid-types/validators-and-types/StytchWorkspaceTypes'

export function useStytchWorkspaces() {
	const fetch = useHoneyGridApiFetch()
	const queryClient = useQueryClient()
	const { member } = useStytchMember()
	const client = useStytchB2BClient()

	// List workspaces
	const workspacesQuery = useQuery({
		queryKey: ['stytch-workspaces', member?.member_id],
		queryFn: () =>
			client.discovery.organizations
				.list()
				.then((res) =>
					res.discovered_organizations.map((discoveredOrg) => discoveredOrg.organization)
				),
		enabled: !!member,
	})

	// Create workspace
	const createWorkspaceMutation = useMutation({
		mutationFn: async (params: CreateStytchWorkspace) => {
			return (await fetch('stytch-workspaces', {
				method: 'POST',
				body: params,
			})) as Promise<Organization>
		},
		onSuccess: (workspace) => {
			queryClient.invalidateQueries({ queryKey: ['stytch-workspaces'] })
			client.session.exchange({
				organization_id: workspace.organization_id,
				session_duration_minutes: 60 * 24 * 30, // 30 days
			})
		},
	})

	// Update workspace
	const updateWorkspaceMutation = useMutation({
		mutationFn: async (params: B2BOrganizationsUpdateOptions) => client.organization.update(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['stytch-workspaces'] })
			queryClient.invalidateQueries({ queryKey: ['current-stytch-workspace'] })
		},
	})

	// Delete workspace
	const deleteWorkspaceMutation = useMutation({
		mutationFn: async () => client.organization.delete(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['stytch-workspaces'] })
		},
	})

	return {
		workspaces: workspacesQuery.data || [],
		isLoading: workspacesQuery.isLoading,
		error: workspacesQuery.error,
		createWorkspace: createWorkspaceMutation.mutate,
		updateWorkspace: updateWorkspaceMutation.mutate,
		deleteWorkspace: deleteWorkspaceMutation.mutate,
		isCreating: createWorkspaceMutation.isPending,
		isUpdating: updateWorkspaceMutation.isPending,
	}
}
